import React, { useState, useEffect } from 'react'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import { Certification } from '../../types/certification'
import { XMarkIcon, PhotoIcon } from '@heroicons/react/24/outline'

interface CertificationDialogProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (certification: Certification) => Promise<void>
  certification?: Certification
}

export const CertificationDialog: React.FC<CertificationDialogProps> = ({
  isOpen,
  onClose,
  onSubmit,
  certification
}) => {
  const [name, setName] = useState(certification?.name || '')
  const [priority, setPriority] = useState(certification?.priority?.toString() || '1')
  const [imageFile, setImageFile] = useState<File | null>(null)
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [imageURL, setImageURL] = useState(certification?.imageURL || '')
  const [imageBase64, setImageBase64] = useState(certification?.image || '')
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState<{ name?: string; priority?: string }>({})

  useEffect(() => {
    if (certification) {
      setName(certification.name)
      setPriority(certification.priority?.toString() || '1')
      setImageURL(certification.imageURL || '')
      setImageBase64(certification.image || '')
      
      // If there's a base64 image, set it as preview
      if (certification.image) {
        setImagePreview(`data:image/png;base64,${certification.image}`)
      } else if (certification.imageURL) {
        setImagePreview(certification.imageURL)
      }
    } else {
      // Reset form for new certification
      setName('')
      setPriority('1')
      setImageFile(null)
      setImagePreview(null)
      setImageURL('')
      setImageBase64('')
    }
  }, [certification, isOpen])

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setImageFile(file)
      
      // Create preview
      const reader = new FileReader()
      reader.onloadend = () => {
        const result = reader.result as string
        setImagePreview(result)
        // Extract base64 part (remove data:image/...;base64, prefix)
        const base64 = result.split(',')[1]
        setImageBase64(base64)
        setImageURL('') // Clear URL when uploading new image
      }
      reader.readAsDataURL(file)
    }
  }

  const removeImage = () => {
    setImageFile(null)
    setImagePreview(null)
    setImageBase64('')
    setImageURL('')
  }

  const validate = () => {
    const newErrors: { name?: string; priority?: string } = {}
    
    if (!name.trim()) {
      newErrors.name = 'Le nom est requis'
    }
    
    if (!priority || isNaN(Number(priority)) || Number(priority) < 1) {
      newErrors.priority = 'La priorité doit être un nombre positif'
    }
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validate()) return

    setSaving(true)
    try {
      const certificationData: Partial<Certification> = {
        id: certification?.id,
        name: name.trim(),
        priority: parseInt(priority)
      }
      
      // Only include image fields if they have changed
      if (isEditing) {
        // Check if image was explicitly removed (removeImage was called)
        const imageWasRemoved = certification && (certification.image || certification.imageURL) && !imageBase64 && !imageURL
        
        if (imageWasRemoved) {
          // User explicitly removed the image - don't send image fields
          delete certificationData.image
          delete certificationData.imageURL
        } else if (imageFile) {
          // User uploaded a new image
          certificationData.image = imageBase64
          // Don't send imageURL when uploading new image
        } else {
          // No image changes - don't send any image fields
          // This is the key: we don't add image or imageURL to certificationData at all
        }
      } else {
        // For new certifications, include image if provided
        if (imageBase64) {
          certificationData.image = imageBase64
        } else if (imageURL) {
          certificationData.imageURL = imageURL
        }
      }
      
      await onSubmit(certificationData as Certification)
      onClose()
    } catch (error) {
      console.error('Error submitting certification:', error)
    } finally {
      setSaving(false)
    }
  }

  if (!isOpen) return null

  const isEditing = !!certification

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            {isEditing ? 'Modifier la certification' : 'Nouvelle certification'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4">
          {/* Image Section */}
          <div>
            <label htmlFor="cert-image-upload" className="block text-sm font-normal text-gray-700 dark:text-gray-300 mb-2">
              Image
            </label>
            <div className="relative inline-block">
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="hidden"
                id="cert-image-upload"
                aria-describedby="cert-image-description"
              />
              <label
                htmlFor="cert-image-upload"
                className="block w-32 h-32 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden cursor-pointer hover:border-gray-400 dark:hover:border-gray-500 transition-colors bg-gray-50 dark:bg-gray-700"
              >
                {imagePreview ? (
                  <img 
                    src={imagePreview} 
                    alt="Preview" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <PhotoIcon className="h-10 w-10 text-gray-400" />
                  </div>
                )}
              </label>
              
              {/* Delete button - same style as ProductDetailsPage */}
              {(imagePreview || imageURL) && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault()
                    removeImage()
                  }}
                  className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 shadow-lg transition-colors"
                  aria-label="Supprimer l'image"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              )}
            </div>
            <div id="cert-image-description" className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Cliquer pour télécharger une image de certification
            </div>
          </div>

          {/* Name Field */}
          <div>
            <label htmlFor="cert-name" className="block text-sm font-normal text-gray-700 dark:text-gray-300 mb-2">
              Nom de la certification <span className="text-red-500">*</span>
            </label>
            <Input
              id="cert-name"
              type="text"
              value={name}
              onChange={(e) => {
                setName(e.target.value)
                if (errors.name) {
                  setErrors({ ...errors, name: undefined })
                }
              }}
              placeholder="Ex: Ecocert"
              className="w-full"
            />
            {errors.name && (
              <p className="text-sm text-red-500 mt-1">{errors.name}</p>
            )}
          </div>

          {/* Priority Field */}
          <div>
            <label htmlFor="cert-priority" className="block text-sm font-normal text-gray-700 dark:text-gray-300 mb-2">
              Priorité <span className="text-red-500">*</span>
            </label>
            <Input
              id="cert-priority"
              type="number"
              value={priority}
              onChange={(e) => {
                setPriority(e.target.value)
                if (errors.priority) {
                  setErrors({ ...errors, priority: undefined })
                }
              }}
              placeholder="1"
              min="1"
              className="w-full"
            />
            {errors.priority && (
              <p className="text-sm text-red-500 mt-1">{errors.priority}</p>
            )}
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Plus le nombre est petit, plus la priorité est élevée
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-4 mt-6">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={saving}
          >
            Annuler
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={saving}
          >
            {saving ? 'Enregistrement...' : isEditing ? 'Modifier' : 'Ajouter'}
          </Button>
        </div>
      </div>
    </div>
  )
}